import { action, computed, observable, runInAction } from 'mobx'
import { isMobile } from 'react-device-detect'
import * as _ from 'lodash'
import React from 'react'

import { StoreExt } from '@utils/reactExt'
import {
  ACCOUNT_TYPE,
  EXCEPTION_TYPE,
  LogoutType,
  PAGE_TYPE,
  Schema$Account,
  Schema$Auth,
  Schema$Certification,
  Schema$MemberInfo,
  Schema$Recommend,
  Schema$SettlementSupport,
  Schema$UserExtraOddsItemMap,
  Schema$UserInfo,
} from '@interface/common'
import { IAuthenticationResult, ISignin } from '@protocol/auth'
import {
  chattingStore,
  clubStore,
  componentStore,
  globalStore,
  joinStore,
  matchupStore,
  portalSocketStore,
  pushwooshStore,
  routerStore,
  userPickStore,
  userStore,
} from '@store/index'
import { message as Message } from 'antd'

import {
  getAllHashParameters,
  getAttachableHashParameters,
  getInitializeClubId,
  isHomePage,
  isSocialAccountType,
  isTutorialVisibleFromCookie,
  isViewPage,
  loadOrReload,
  makeAuthenticationUrlByType,
  makeDetailMessage,
  showToast,
  TOAST_TYPE,
} from '@utils/utils'
import { DUPLICATE_REQUEST_EXCEPTION, JOIN_URL } from '@constants/index'
import { isShowAttendancePopup } from '@views/Popup/Event/Attendance'
import { AUTHENTICATION_TYPE, PLAY_BLOCK_TYPE, PROPERTY_ID } from 'adv-protocol/src/GameCommon'
import { ERR } from 'adv-protocol/src/ErrorCodes'
import { getAuthenticationErrorText } from '@game/errtext'
import { GlobalStoreKey } from '@v2/constants/common'
import StorageUtils from '@utils/storage-utils'
import DialogUtils from '@utils/dialog-utils'
import PopupUtils from '@utils/popup-utils'

export type LoginParams = ISignin.Params
export type SocialLoginParams = ISignin.SocialLoginParams
export type AuthKeyLoginParams = ISignin.loginByAuthKey
export type UserInfo = Schema$UserInfo
export type MemberInfo = Schema$MemberInfo
export type CertificationInfo = Schema$Certification
export type AuthInfo = Schema$Auth
export type RecommendInfo = Schema$Recommend
export type AccountInfo = Schema$Account
export type SettlementSupport = Schema$SettlementSupport

export class AuthStore extends StoreExt {
  /**
   * 用户信息
   *
   * @type {UserInfo}
   * @memberof AuthStore
   */
  @observable
  userInfo: UserInfo = {}

  @observable
  memberInfo?: MemberInfo

  @observable
  certificationInfo?: CertificationInfo

  @observable
  authInfo?: AuthInfo

  @observable
  recommendInfo?: RecommendInfo

  @observable
  accountInfo?: AccountInfo

  @observable
  signedin: boolean = undefined

  @observable
  isSpecificClubMember = false

  @observable
  isForceShowLoginPopup = false

  adultAuthenticationResultHandler = null

  constructor() {
    super()

    if (!window.v2) {
      window.v2 = {}
    }

    if (!window.v2.authStore) {
      window.v2.authStore = {}
    }

    window.v2.authStore.loginByNaver = this.loginByNaver
  }

  goHomePageAdultCertification = (authKey: string) => {
    const hashParameters = getAttachableHashParameters(true)
    let v1HomepageUrl = ''
    if (authKey.length !== 0) {
      v1HomepageUrl = `${isMobile ? process.env.HOMEPAGE_MO : process.env.HOMEPAGE_PC}/#/auth_login/${authKey}/adultauth${hashParameters}`
    } else {
      v1HomepageUrl = `${isMobile ? process.env.HOMEPAGE_MO : process.env.HOMEPAGE_PC}/#/main${hashParameters}`
    }
    globalStore.toMoveWebHomePage(v1HomepageUrl, 'self')
  }

  @action
  login = async (params: LoginParams, executeChangeClub = true) => {
    try {
      const res = await this.api.auth.login(params)
      await this.loginResultProcess(res, executeChangeClub, ACCOUNT_TYPE.LOCAL)
    } catch (err) {
      globalStore.resetGeneralPopupProperties()
      await this.reset()
    }
  }

  @action
  loginByGoogle = async (params: SocialLoginParams, executeChangeClub = true) => {
    try {
      const res = await this.api.auth.loginByGoogle(params)
      await this.loginResultProcess(res, executeChangeClub, ACCOUNT_TYPE.GOOGLE, params)
    } catch (err) {
      globalStore.resetGeneralPopupProperties()
      await this.reset()
    }
  }

  @action
  loginByKakao = async (params: SocialLoginParams, executeChangeClub = true) => {
    const isAccountTransferEnabled = globalStore.isAccountTransferEnabled()
    try {
      const res = await this.api.auth.loginByKakao(params)

      const loginResult = async () => {
        await this.loginResultProcess(res, executeChangeClub, ACCOUNT_TYPE.KAKAO, params)
      }

      if (isAccountTransferEnabled && _.isEmpty(res.error)) {
        if (_.isEmpty(res.user)) {
          DialogUtils.showKakaoNewUserInfo()
        } else {
          const { id: userPromiseId } = res.user
          await userStore.showAccountTransferInfo(userPromiseId, ACCOUNT_TYPE.KAKAO, loginResult)
        }
        return
      } else {
        await loginResult()
      }
    } catch (err) {
      globalStore.resetGeneralPopupProperties()
      await this.reset()
    }
  }

  @action
  loginByNaver = async (accessToken, executeChangeClub = true) => {
    let result
    try {
      result = await this.api.auth.getNaverMemberProfile({ accessToken })
      const params = {
        username: result.username,
        password: 'null',
        email: result.email,
        accessToken,
      }
      const res = await this.api.auth.loginByNaver(params)
      await this.loginResultProcess(res, executeChangeClub, ACCOUNT_TYPE.NAVER, params)
    } catch (err) {
      console.error('[naverLogin] - loginByNaverErr', err)
      globalStore.resetGeneralPopupProperties()
      await this.reset()
    }
    return result
  }

  @action
  loginByApple = async (params: SocialLoginParams, executeChangeClub = true) => {
    try {
      const res = await this.api.auth.loginByApple(params)
      await this.loginResultProcess(res, executeChangeClub, ACCOUNT_TYPE.APPLE, params)
    } catch (err) {
      globalStore.resetGeneralPopupProperties()
      await this.reset()
    }
  }

  @action
  loginByAuthKey = async (params: AuthKeyLoginParams) => {
    try {
      const res = await this.api.auth.loginByAuthKey(params)

      await this.loginResultProcess(
        res,
        true,
        ACCOUNT_TYPE.AUTH_KEY,
        null,
        res?.user?.clubId
      )
    } catch (err) {
      await this.reset()
    }
  }

  @action
  loginResultProcess = async (
    res: ISignin.Schema,
    executeChangeInitializeClub: boolean,
    accountType: ACCOUNT_TYPE,
    params?: SocialLoginParams,
    clubId?: string
  ) => {
    const { user, error, detail, errcode } = res
    globalStore.resetGeneralPopupProperties()
    globalStore.closeDialog()
    globalStore.setVisibleSideBar(false)

    if (user) {
      // userStore.registerPushwooshHwid()
      pushwooshStore.registerUserHWID().catch(console.error)
      chattingStore.changeChattingModalVisible(false).catch(console.error)

      if (!globalStore.globalSettings) {
        await globalStore.getGlobalSettings()
      }

      matchupStore.resetClubInfo()
      matchupStore.resetTournamentFilterList()
      this.setUser(user, true)
      StorageUtils.deleteLastClubId()
      StorageUtils.deleteAllChatEmoticonList()

      const currentPath = routerStore.location.pathname
      if (!_.isEmpty(clubId)) {
        const targetClubId = getInitializeClubId(clubId)
        const moveToMatchupPage = currentPath !== `/matchup/cross/${targetClubId}`
        matchupStore.changeClubId(targetClubId, undefined, moveToMatchupPage)
      } else if (executeChangeInitializeClub) {
        const targetClubId = getInitializeClubId()
        const moveToMatchupPage = !isHomePage() && currentPath !== `/matchup/cross/${targetClubId}`
        matchupStore.changeClubId(targetClubId, undefined, moveToMatchupPage)
      }

      userStore.getUserGameOption().catch(console.error)
      await this.fetchUser()
      userPickStore.fetchLastBestUserPickId().catch(console.error)
      matchupStore.fetchFavoriteTotalPreMatchCount().catch(console.error)
      componentStore.fetchMatchupNotices().catch(console.error)
      componentStore.fetchExtraOddsItemDetails().catch(console.error)
      userStore.fetchUserTitleDefs().catch(console.error)
      clubStore.fetchSimpleClubs().catch(console.error)
      chattingStore.resetAllMessage()
    } else if (error) {
      if (error === EXCEPTION_TYPE.USER_NICK_NAME_NOT_FOUND || error === EXCEPTION_TYPE.CERTIFICATION_NOT_FOUND) {
        this.showAdultAuthenticationRequestDialog(detail)
      } else if (error === EXCEPTION_TYPE.ACCOUNT_DUPLICATED) {
        this.showGoogleAccountUnifiedDialog(detail)
      } else if (error === EXCEPTION_TYPE.SERVER_STATUS) {
        this.showServerStatusDialog(detail)
        if (accountType === ACCOUNT_TYPE.AUTH_KEY) {
          loadOrReload('/')
        }
      } else if (error === EXCEPTION_TYPE.AUTHENTICATION_FAILURE && errcode === ERR.YOU_ARE_BLOCKED) {
        this.showUserBlockedDialog()
      } else {
        Message.info(makeDetailMessage(detail))
      }
    } else if (isSocialAccountType(accountType)) {
      joinStore.setAccountType(accountType)
      joinStore.setSocialLoginParams(params)
      loadOrReload(`/agree${getAllHashParameters(true)}`)
    }
  }
  @action
  logout = async (logoutType = LogoutType.USER, redirect = true) => {
    try {
      const { id } = this.userInfo
      await this.api.auth.logout({ userPromiseId: id, logoutType })
      StorageUtils.deleteLastClubId()
      this.reset()
      userStore.setUserDetails(null)
      chattingStore.resetChatBlockUserIds()
    } catch (err) {
      console.error(err)
    }

    if (redirect) {
      window.location.replace('/')
    }
  }

  @action
  reset = () => {
    this.setUser({})
    this.setMember(undefined)
    globalStore.setVisibleSideBar(false)
    globalStore.resetGeneralPopupProperties()
    matchupStore.resetClubInfo()
  }

  /**
   * 유저의 기본정보 + 손실한도 체크 요청 + 신의 가호 아이템 수 반환
   */
  @action
  refreshSimpleUserInfo = async (checkPlayBlock?: boolean) => {
    try {
      const res = await this.api.user.getSimpleUserInfo()
      const { error } = res
      if (!res || error === DUPLICATE_REQUEST_EXCEPTION) {
        return
      }

      const { user } = res
      // delete user.avatar
      const newUserInfo = _.cloneDeep(this.userInfo)
      Object.assign(newUserInfo, user)

      this.setUser(newUserInfo)

      if (checkPlayBlock && this.checkUserPlayBlock()) {
        return
      }
    } catch (err) {
      console.error(err)
    }
  }

  @action
  checkUserPlayBlock = (refresh?: boolean) => {
    if (this.userInfo?.isPlayBlock) {
      if (this.userInfo?.playBlockType === PLAY_BLOCK_TYPE.LOSS_LIMIT_EXCEEDED) {
        if (refresh) {
          globalStore.pushDialogOk({
            title: '알림',
            text: ['1일 손실한도를 초과했습니다.', '6시간 동안 게임 접속이 제한됩니다.'],
            className: 'small-size',
            callbackCloseDialog: () => {
              globalStore.closeDialog()
              globalStore.setVisibleNoAccess(true)
            },
          })
        } else {
          globalStore.setVisibleNoAccess(true)
        }
      } else {
        globalStore.setVisibleNoAccess(true)
      }
      return true
    }
    return false
  }

  @action
  refreshUserChatOption = async (checkPlayBlock?: boolean) => {
    try {
      const res = await this.api.user.getChatOption()
      const { user } = res
      delete user.avatar
      const newUserInfo = _.cloneDeep(this.userInfo)
      Object.assign(newUserInfo, user)
      this.setUser(newUserInfo)

      if (checkPlayBlock && this.checkUserPlayBlock()) {
        return
      }
    } catch (err) {
      console.error(err)
    }
  }

  @action
  fetchUser = async (refresh = false, forceClubId?: string) => {
    try {
      const res = await this.api.user.getUserInfo({})
      const { error } = res
      if (!res || error === DUPLICATE_REQUEST_EXCEPTION) {
        return
      }

      if (!res) {
        await this.reset()

        const globalValue = globalStore.findGlobalSetting(GlobalStoreKey.DEFAULT_GUEST_CLUB)?.value
        const guestClubId = globalValue ?? process.env.DEFAULT_GUEST_CLUB_ID
        // 회원가입 페이지 url 인 경우는 매치업 리스트 화면으로 변경되지 않아야 한다.
        if (_.toLower(window.location.hash).indexOf(JOIN_URL.AGREE) === -1 && !this.isSignedIn) {
          await matchupStore.changeClubId(guestClubId)
        }

        return
      }

      const {
        user,
        member,
        certification,
        account,
        auth,
        settlementSupport,
        unreadMessageCount,
        unQuickReadMessageCount,
        showTutorialPopup,
        isShowGmoneyChargeInducePopup,
        isShowMatchupChargeButton,
        hasDiaBettingHistory,
        hasCasinoLoginHistory,
        firstJoinAtByCivalue,
        recommend,
        noticeBadgeEnabled,
        cheerForTeamUserActivityScoreLastTime,
        userActivityInfo,
      } = res

      this.setUser(user)
      this.setMember(member)
      this.setCertification(certification)
      this.setAuth(auth)
      this.setAccount(account)
      this.setRecommend(recommend)
      if (_.isNil(userStore.notificationData.unreadMessageCount)) {
        userStore.setUnreadMessageCount(unreadMessageCount)
      }
      userStore.setUnQuickReadMessageCount(unQuickReadMessageCount)
      userStore.setSettlementSupport(settlementSupport)
      userStore.setShowTutorialPopup(showTutorialPopup)
      userStore.setHasDiaBettingHistory(hasDiaBettingHistory)
      userStore.setHasCasinoLoginHistory(hasCasinoLoginHistory)
      userStore.setFirstJoinAtByCivalue(firstJoinAtByCivalue)
      userStore.setIsShowMatchupChargeButton(isShowMatchupChargeButton)
      userStore.setCheerForTeamUserActivityScoreLastTime(cheerForTeamUserActivityScoreLastTime)
      componentStore.setNoticeBadgeEnabled(noticeBadgeEnabled)
      globalStore.setUserActivityInfo(userActivityInfo)

      if (this.checkUserPlayBlock(refresh)) {
        return
      }

      globalStore.setVisibleNoAccess(false)

      if (!refresh) {
        const userInfo = this.userInfo
        const pathName = routerStore.location.pathname
        if (!_.isEmpty(_.trim(forceClubId))) {
          await matchupStore.changeClubId(forceClubId)
        } else if (isViewPage(PAGE_TYPE.MATCHUP)) {
          const [, , tabName, clubId] = pathName.split('/')
          if (_.isNil(tabName) || _.isNil(clubId)) {
            // routerStore.replace(`/matchup/cross/${userInfo.clubId}`)
            loadOrReload(`/matchup/cross/${userInfo.clubId}`, false)
            await matchupStore.changeClubId(userInfo.clubId, 'cross', false)
          } else if (clubId && !userInfo.isSpecificClubMember && process.env.SPECIFIC_CLUB_IDS.includes(clubId)) {
            loadOrReload(`/matchup/${tabName}/${process.env.DEFAULT_CLUB_ID}`, false)
            await matchupStore.changeClubId(process.env.DEFAULT_CLUB_ID, tabName, false)
          } else if (clubId && userInfo.isSpecificClubMember && userInfo.clubId !== clubId) {
            loadOrReload(`/matchup/${tabName}/${userInfo.clubId}`, false)
            await matchupStore.changeClubId(userInfo.clubId, tabName, false)
          } else {
            loadOrReload(`/matchup/${tabName}/${clubId}`, false)
            await matchupStore.changeClubId(clubId, tabName, false)
          }
        } else if (pathName) {
          loadOrReload(pathName)
        } else {
          loadOrReload(`/matchup/cross/${userInfo.clubId}`, false)
          await matchupStore.changeClubId(userInfo.clubId, 'cross', false)
        }
      }

      portalSocketStore.registerClient()
      chattingStore.fetchChatBlockUserIds().catch(console.error)
      if (_.isNil(componentStore.isPossibleInvitor)) {
        componentStore.fetchIsPossibleInvitor()
      }

      if (joinStore.isJoinUrl) {
        return
      }

      this.showGuideBonusPopup()
      this.showLoginRequiredPopupFlow(isShowGmoneyChargeInducePopup).catch(console.error)
    } catch (err) {
      await this.reset()
    }
  }

  checkForefrontPopupTarget = async (): Promise<boolean> => {
    const isShow = await componentStore.fetchForefrontPopups()
    if (!isShow) {
      globalStore.setVisibleForefrontPopup(false)
    }
    return isShow
  }

  showGuideBonusPopup = () => {
    const isEnabledGuideBonus = globalStore.getGuideBonusEnabled()
    const { settlementSupport } = userStore
    if (settlementSupport && isEnabledGuideBonus) {
      globalStore.setVisibleGuideBonusReceiveButton(!settlementSupport.guideBonusReceived)
      // 더 이상 사용하지 않는 팝업이라 필요없음
      // globalStore.setVisibleGuideNoticePopup(settlementSupport.showGuidePopup)
    }
  }

  showTutorialPopup = () => {
    const isEnabledTutorial = globalStore.getTutorialEnabled()
    const { showTutorialPopup } = userStore

    if (isEnabledTutorial) {
      const isTutorialVisible = isTutorialVisibleFromCookie() && showTutorialPopup
      globalStore.setVisibleTutorialPopup(isMobile && isTutorialVisible)
    } else {
      globalStore.setVisibleTutorialPopup(false)
    }
  }

  showAttendancePopup = async () => {
    if (!componentStore.attendance) {
      const isShow = await isShowAttendancePopup(componentStore, this.certificationInfo)
      if (isShow) {
        // if (globalStore.collapsedNoticePopup) {
        //   globalStore.toggleNoticePopupVisible(false)
        // }
        globalStore.setVisibleAttendance(true)
      }
    }
  }

  checkUsageGuideVideoAutoTarget = async (): Promise<boolean> => {
    if (!this.isUsageGuideVideoAutoTargetEligible()) {
      return false
    }

    return this.refreshUsageGuideVideoAutoTarget()
  }

  isUsageGuideVideoAutoTargetEligible = (): boolean => {
    const options = globalStore.getUsageGuideVideoPopupOptions()
    if (!options.enabled || !options.autoDisplayEnabled || !options.videoFileName) {
      return false
    }

    if (StorageUtils.isUsageGuideVideoWatched()) {
      return false
    }

    return true
  }

  checkUsageGuideVideoAutoTargetFromStore = (): boolean => {
    return this.isUsageGuideVideoAutoTargetEligible() && userStore.isUsageGuideVideoAutoTarget
  }

  refreshUsageGuideVideoAutoTarget = async (): Promise<boolean> => {
    try {
      const response = await this.api.user.getUsageGuideVideoAutoTarget()
      const isUsageGuideVideoAutoTarget = response?.isUsageGuideVideoAutoTarget === true
      userStore.setIsUsageGuideVideoAutoTarget(isUsageGuideVideoAutoTarget)
      return isUsageGuideVideoAutoTarget
    } catch (err) {
      console.error('[AuthStore.refreshUsageGuideVideoAutoTarget]', err)
      userStore.setIsUsageGuideVideoAutoTarget(false)
      return false
    }
  }

  // 한 단계의 판정 실패가 나머지 단계의 자동 노출까지 막지 않도록, 실패한 단계는
  // 대상에서 제외(false)하고 계속 진행한다.
  safeCheckLoginAutoPopupTarget = async (check: () => boolean | Promise<boolean>): Promise<boolean> => {
    try {
      return await check()
    } catch (err) {
      console.error(err)
      return false
    }
  }

  /**
   * 로그인 완료 후 팝업을 표시한다. 튜토리얼·출석체크·충전유도·전면공지·손실한도는 기존처럼
   * 각자 판정과 동시에 열리고 서로 순서를 기다리지 않는다(z-index로 관리). 이용방법만 예외로,
   * 전면공지·손실한도 중 무엇이 뜨는지에 따라 여는 시점이 달라진다.
   *   - 손실한도가 뜬다면(전면공지 여부 무관): 손실한도가 닫힐 때 연다.
   *   - 손실한도는 안 뜨고 전면공지만 뜬다면: 전면공지가 닫힐 때 연다.
   *   - 둘 다 안 뜬다면: 즉시 연다.
   * 이용방법이 닫힌 뒤에는 authStore가 더 할 일이 없어서(출석체크·충전유도는 이용방법과 무관하게
   * z-index로 독립 관리) 닫힘 콜백을 별도로 안 둔다.
   */
  showLoginRequiredPopupFlow = async (isShowGmoneyChargeInducePopup: boolean) => {
    this.showTutorialPopup()
    this.showAttendancePopup().catch(console.error)
    this.showChargingCompletePopup(isShowGmoneyChargeInducePopup)

    const [isForefrontActive, isLossLimitActive, isUsageGuideVideoActive] = await Promise.all([
      this.safeCheckLoginAutoPopupTarget(() => this.checkForefrontPopupTarget()),
      this.safeCheckLoginAutoPopupTarget(() => componentStore.checkRequireLossLimitPopupTarget()),
      this.safeCheckLoginAutoPopupTarget(() => this.checkUsageGuideVideoAutoTarget()),
    ])

    // 전면공지·손실한도는 대상이면 각자 독립적으로 열되, 닫힘 신호만 받아둔다.
    const forefrontClosed = new Promise<void>(resolve => {
      if (!isForefrontActive || !globalStore.openForefrontPopup(resolve)) {
        resolve()
      }
    })
    const lossLimitClosed = new Promise<void>(resolve => {
      if (isLossLimitActive) {
        PopupUtils.showLossLimitSettingPopup(() => setTimeout(resolve, 0))
      } else {
        resolve()
      }
    })

    if (isUsageGuideVideoActive) {
      if (isLossLimitActive) {
        await lossLimitClosed
      } else if (isForefrontActive) {
        await forefrontClosed
      }
      PopupUtils.showUsageGuideVideo({ entry: 'login' })
    }
  }

  showChargingCompletePopup = (showGmoneyChargeInducePopup: boolean) => {
    const isEnabledGmoneyChargeInducePopup = globalStore.getGmoneyChargeInducePopupEnabled()
    if (isEnabledGmoneyChargeInducePopup && showGmoneyChargeInducePopup) {
      globalStore.setVisibleChargingComplete(true)
    }
  }

  @action
  changeUserAvatar = (userProfileImg: string, itemCharacterLinkIndex?: number) => {
    if (userProfileImg) {
      this.userInfo.avatar = userProfileImg
    } else if (!_.isNil(itemCharacterLinkIndex)) {
      const userItems = userStore.userItems
      const item = _.find(userItems, {
        propertyId: PROPERTY_ID.AVATAR, link_index: itemCharacterLinkIndex,
      })
      if (item) {
        this.userInfo.avatar = `https://d1i7hydgfclv7m.cloudfront.net/data_center/item_image_m/${item.icon_m}.png`
      }
    }
  }

  @action
  setUser = (userInfo: UserInfo, isLoginResultProcess?: boolean): UserInfo => {
    if (userInfo?.avatar?.indexOf('randomuser.me/api/portraits/men/46.jpg') > -1) {
      userInfo.avatar = 'https://d1i7hydgfclv7m.cloudfront.net/data_center/item_image_m/FB24E425-3167-4AA6-AC12-56CA9DECEC7A.png'
    }
    this.userInfo = userInfo
    this.signedin = _.isString(userInfo.id) && _.trim(userInfo.id) !== ''

    chattingStore.initChatSocket().catch(console.error)

    if (this.signedin) {
      // eslint-disable-next-line dot-notation
      const android = window.android
      if (android) {
        android.jnRequestVerify()
      }
      componentStore.getGiftBox({}).catch(console.error)
    }
    if (this.userInfo) {
      this.isSpecificClubMember = _.isNil(this.userInfo.isSpecificClubMember)
        ? false : this.userInfo.isSpecificClubMember
    }
    if (!this.userInfo.clubId) {
      const representativeClubId = globalStore.findGlobalSetting(GlobalStoreKey.REPRESENTATIVE_CLUB)?.value ?? null
      this.userInfo.clubId = representativeClubId || process.env.DEFAULT_CLUB_ID
    }
    return userInfo
  }

  @action
  setCertification = (certificationInfo: CertificationInfo): CertificationInfo => {
    this.certificationInfo = certificationInfo
    return certificationInfo
  }

  @action
  setAuth = (authInfo: AuthInfo): AuthInfo => {
    this.authInfo = authInfo
    StorageUtils.saveAuthKey(authInfo.authKey)
    return authInfo
  }

  @action
  setAccount = (accountInfo: AccountInfo): AccountInfo => {
    this.accountInfo = accountInfo
    return accountInfo
  }

  @action
  setRecommend = (recommend: RecommendInfo): RecommendInfo => {
    this.recommendInfo = recommend
    return recommend
  }

  @action
  updateUserAssets = (update: UserInfo): UserInfo => {
    const user = _.clone(this.userInfo)
    user.money = update.money ?? user.money
    user.heart = update.heart ?? user.heart
    user.mileage = update.mileage ?? user.mileage
    user.point = update.point ?? user.point
    user.ticket = update.ticket ?? user.ticket
    user.dia = update.dia ?? user.dia
    user.cash = update.cash ?? user.cash
    user.userExtraOddsItems = update.userExtraOddsItems ?? user.userExtraOddsItems
    this.userInfo = user
    return user
  }

  @action
  setMember = (memberInfo: MemberInfo): MemberInfo => {
    this.memberInfo = memberInfo
    return memberInfo
  }

  @computed
  get isSignedIn() {
    return this.signedin === true
  }

  checkAdultAuthentication = async () => {
    if (!this.isSignedIn) {
      return
    }

    const result = await this.api.auth.checkAdultAuthenticationExpire({})
    if (result && result?.isExpire) {
      this.showAdultAuthenticationExpireDialog()
      globalStore.setVisibleSideBar(false)
      globalStore.resetGeneralPopupProperties()
    }
  }

  @action
  changeUserChatOption(key: string, value: any) {
    this.userInfo.chatOption[key] = value
    this.api.user.updateChatOption(this.userInfo.chatOption)
  }

  showAdultAuthenticationExpireDialog = () => {
    globalStore.pushDialogOk({
      id: 'identityVerificationDialog',
      title: '성인인증 만료',
      text: ['성인인증 기간이 만료되었습니다. 인증 페이지로 이동합니다.'],
      className: 'small-size',
      callbackCloseDialog: () => {
        loadOrReload('/identityVerification')
        globalStore.closeDialog()
      },
    })
  }

  showAdultAuthenticationRequestDialog = (detail: string) => {
    globalStore.pushDialogYesNo({
      id: 'homePageAdultCertificationDialog',
      title: '성인인증 요청',
      text: ['승부사 홈페이지를 통해 로그인 후 성인인증을 해야 합니다. 이동하시겠습니까?'],
      className: 'small-size',
      callbackPositive: () => {
        this.goHomePageAdultCertification(detail)
      },
    })
  }

  showGoogleAccountUnifiedDialog = (detail: string) => {
    globalStore.pushDialogYesNo({
      id: 'duplicateGoogleAccountDialog',
      title: '구글 계정 중복',
      text: ['구글 계정이 한개 이상 존재합니다.', '승부사 홈페이지를 통해 계정 통합 후 이용가능 합니다.', '이동하시겠습니까?'],
      className: 'small-size',
      callbackPositive: () => {
        const url = `${isMobile ? process.env.HOMEPAGE_MO : process.env.HOMEPAGE_PC}/#/auth_login/${detail}/unified`
        globalStore.toMoveWebHomePage(url, 'self')
      },
    })
  }

  showServerStatusDialog = (detail: string) => {
    globalStore.pushDialogOk({
      id: 'notificationServerCheckDialog',
      title: '서버 점검',
      text: [detail],
      className: 'small-size',
    })
  }

  showNotSupportedBrowserDialog = () => {
    globalStore.pushDialogOk({
      id: 'notSupportBrowserAlertDialog',
      title: '알림',
      text: ['로그인을 지원하지 않는 브라우저입니다.', '다른 브라우저를 이용해 주십시오.'],
      className: 'small-size',
      callbackCloseDialog: () => {
        globalStore.closeDialog()
      },
    })
  }

  showUserBlockedDialog = () => {
    globalStore.pushDialogOk({
      id: 'notAllowedLoginDialog',
      title: '로그인이 제한되었습니다.',
      text: ['이용제한 규정에 따라 이용제한되었습니다.', '운영정책을 참고해주시고, 이메일(help@adventurer.co.kr)을 통해 문의주시기 바랍니다.'],
      className: 'small-size',
      callbackCloseDialog: () => {
        globalStore.closeDialog()
      },
    })
  }

  setAdultAuthenticationResultHandler(handler: (result: IAuthenticationResult.Schema) => void) {
    this.adultAuthenticationResultHandler = handler
  }

  showAuthenticationPopup(type: AUTHENTICATION_TYPE, handler?: (result: IAuthenticationResult.Schema) => void) {
    globalStore.closeAuthenticationPopup()

    const url = makeAuthenticationUrlByType(type)
    const options = 'width=500, height=550, top=100, left=100, fullscreen=no, menubar=no, status=no, toolbar=no, titlebar=yes, location=no, scrollbar=no'
    const popup = window.open(url, `AUTH_POPUP_${new Date().getTime()}`, options)
    globalStore.setAuthenticationPopup(popup)

    if (handler) {
      this.setAdultAuthenticationResultHandler(handler)
    }
  }

  validateAuthenticationResult(result: IAuthenticationResult.Schema): boolean {
    const { resultCode, status } = result

    if (!status && !_.isEmpty(result?.resultMsg)) {
      showToast(TOAST_TYPE.WARN, result.resultMsg)
      return false
    }

    if (resultCode !== ERR.OK) {
      const errorText = getAuthenticationErrorText(resultCode)
      showToast(TOAST_TYPE.WARN, errorText)
      return false
    }

    return true
  }

  @action
  showLoginPopup(body: React.ReactElement): void {
    globalStore.addGeneralPopupProperties(body, '로그인', {
      wrapClassName: '',
      className: 'login-popup',
      isScroll: true,
      custom: false,
      clsBtn: false,
    }, null, 'login-popup')
  }

  getUserExtraOddsItems(): Schema$UserExtraOddsItemMap {
    return this.userInfo?.userExtraOddsItems
  }

  async updateUserTitleEnabled(): Promise<void> {
    try {
      const result = await this.api.user.updateUserTitleEnabled({
        enabled: !this.userInfo.userTitleEnabled,
      })

      const { enabled } = result
      runInAction(() => {
        this.userInfo.userTitleEnabled = enabled
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setIsForceShowLoginPopup(value: boolean): void {
    this.isForceShowLoginPopup = value
  }
}

export default new AuthStore()
